import { resolveAccCurrencyRate } from '../../../services/mutations';
import { applyDocumentLinePatch } from './balanceUtils';
import type { DocumentLineForm } from './documentLineTypes';
import type { LinkPickerSelection } from './linkPickerNormalize';

type CurrencyOption = { value: string; label: string };

const round2 = (n: number) => Math.round(n * 100) / 100;

export const resolveSelectionOpenBalance = (selection: LinkPickerSelection): number => {
  if (selection.openBalance != null && selection.openBalance > 0.005) {
    return round2(selection.openBalance);
  }
  if (selection.debitLocal > 0.005) return round2(selection.debitLocal);
  return 0;
};

export const resolveCurrencyId = (currencyCode: string | null | undefined, currencyOptions: CurrencyOption[]): string => {
  if (!currencyCode?.trim()) return '';
  const code = currencyCode.trim().toUpperCase();
  const match = currencyOptions.find((c) => c.label.trim().toUpperCase() === code);
  return match?.value ?? '';
};

export const resolveRateForCurrency = async (
  currencyId: string,
  postingDate: string,
  cache: Map<string, { rate: number | null; rateDate: string | null }>,
): Promise<string> => {
  if (!currencyId) return '';
  const cacheKey = `${currencyId}:${postingDate || ''}`;
  try {
    let resolved = cache.get(cacheKey);
    if (!resolved) {
      const result = await resolveAccCurrencyRate(Number(currencyId), postingDate || undefined);
      resolved = { rate: result.rate, rateDate: result.rateDate };
      cache.set(cacheKey, resolved);
    }
    if (resolved.rate == null || !(resolved.rate > 0)) return '';
    return String(resolved.rate);
  } catch {
    return '';
  }
};

export const buildLineFromLinkSelection = async (
  base: DocumentLineForm,
  selection: LinkPickerSelection,
  ctx: {
    postingDate: string;
    currencies: CurrencyOption[];
    rateCache: Map<string, { rate: number | null; rateDate: string | null }>;
  },
): Promise<DocumentLineForm> => {
  const currencyId = resolveCurrencyId(selection.currencyCode, ctx.currencies);
  const rate = await resolveRateForCurrency(currencyId, ctx.postingDate, ctx.rateCache);
  const openBalance = resolveSelectionOpenBalance(selection);
  const payAmount =
    selection.payAmount != null && selection.payAmount > 0.005
      ? round2(selection.payAmount)
      : openBalance;
  const paymentLocal = payAmount > 0.005 ? payAmount.toFixed(2) : '';

  const linkNumber = selection.linkNumber?.trim() || '';

  return applyDocumentLinePatch(base, {
    currencyId,
    rate,
    ...(linkNumber ? { linkNumber } : {}),
    narrative: selection.narrative,
    ...(selection.dueDate ? { dueDate: selection.dueDate } : {}),
    debitLocal: '',
    debitUSD: '',
    creditLocal: paymentLocal,
    openBalance: openBalance > 0.005 ? openBalance.toFixed(2) : '',
  });
};
